import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardActionArea,
  CardContent,
  useMediaQuery,
  useTheme,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  Alert,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  CleaningServices,
  Home,
  Business,
  Pool,
  Deck,
  Construction,
  MeetingRoom,
  LocalCarWash,
  Shield,
  Build,
  Apartment,
  Kitchen,
  Microwave,
  Window,
  LocalLaundryService,
  BorderBottom,
  Toys,
  Blinds,
  FormatPaint,
  AccessTime,
  HomeWork,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import QuoteSummaryCard from './QuoteSummaryCard';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import EditableInvoiceQuoteCard from './EditableInvoiceQuoteCard';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import PageLayout from './PageLayout';
import SectionBlock from './SectionBlock';
import { STRIPE_PUBLISHABLE_KEY, STRIPE_ENABLED } from '../config/stripe';

const iconMap = {
  CleaningServices,
  Home,
  Business,
  Pool,
  Deck,
  Construction,
  MeetingRoom,
  LocalCarWash,
  Shield,
  Build,
  Apartment,
  Kitchen,
  Microwave,
  Window,
  LocalLaundryService,
  BorderBottom,
  Toys,
  Blinds,
  FormatPaint,
  AccessTime,
  HomeWork
};

const cityCode = process.env.REACT_APP_CITY_CODE || 'MCO';
const tenantID = process.env.REACT_APP_TENANT_ID ? parseInt(process.env.REACT_APP_TENANT_ID) : 1;

const propertyTypes = [
  { value: 'HOUSE', label: 'House' },
  { value: 'APARTMENT', label: 'Apartment' },
  { value: 'CONDO', label: 'Condo' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'OTHER', label: 'Other' },
];

const frequencyOptions = [
  { label: 'One-time', value: 'ONE_TIME' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Bi-weekly', value: 'BIWEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
];

const steps = [
  'Contact Info',
  'Type of Cleaning',
  'Property Details',
  'Schedule',
  'Extras',
  'Book',
];

const squareFootageOptions = [
  { label: 'Less than 500 sq ft', value: '<500' },
  { label: '500 - 999 sq ft', value: '500-999' },
  { label: '1,000 - 1,499 sq ft', value: '1000-1499' },
  { label: '1,500 - 1,999 sq ft', value: '1500-1999' },
  { label: '2,000 - 2,499 sq ft', value: '2000-2499' },
  { label: '2,500 - 2,999 sq ft', value: '2500-2999' },
  { label: '3,000+ sq ft', value: '3000+' },
];

const stripePromise = STRIPE_ENABLED && STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const StripePaymentForm = ({ amount, onSuccess, onError, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Step 1: Create payment intent on backend (using the working endpoint)
      const createIntentResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: Math.round(Number(amount) * 100), // Convert to cents
          currency: 'usd',
          description: 'Cleaning Service Payment'
        }),
      });

      if (!createIntentResponse.ok) {
        throw new Error('Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = await createIntentResponse.json();

      // Step 2: Confirm the payment with Stripe
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
        onError && onError(result.error.message);
      } else {
        // Step 3: Verify payment status on backend
        const confirmResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntentId
          }),
        });

        if (!confirmResponse.ok) {
          throw new Error('Failed to confirm payment');
        }

        const confirmResult = await confirmResponse.json();
        
        if (confirmResult.success) {
          setProcessing(false);
          onSuccess && onSuccess(confirmResult);
        } else {
          setError(confirmResult.error || 'Payment failed');
          setProcessing(false);
          onError && onError(confirmResult.error || 'Payment failed');
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
      onError && onError(err.message || 'Payment failed. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <Typography variant='body2' sx={{ mb: 1 }}>Card Details</Typography>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' },
                padding: '10px 12px',
              },
              invalid: { color: '#c23d4b' },
            },
          }}
          onChange={e => setCardComplete(e.complete)}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        sx={{ fontSize: '1.2rem', py: 2, mt: 2 }}
        disabled={processing || !cardComplete || disabled || !stripe}
      >
        {processing ? 'Processing Payment...' : `Confirm & Book My Cleaning for $${amount}`}
      </Button>
    </form>
  );
};

const QuotationRequest = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const location = useLocation();
  const selectedService = location.state?.selectedService;

  const [formData, setFormData] = useState({
    mainServiceIds: selectedService ? [selectedService.id] : [],
    extraServiceIds: [],
    propertyType: '',
    bedrooms: 1,
    bathrooms: 1,
    squareFootage: '',
    poolSize: '',
    exteriorFlooringSize: '',
    yardSize: '',
    frequency: '',
    address: '',
    zipcode: '',
    city: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    specialInstructions: '',
    isRecurring: false,
    recurringPattern: null,
    customerName: '',
    contactInfo: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showRegister, setShowRegister] = useState(false);
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [booking, setBooking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [quotationId, setQuotationId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedService && !formData.mainServiceIds.includes(selectedService.id)) {
      setFormData(prev => ({
        ...prev,
        mainServiceIds: [selectedService.id]
      }));
    }
    // eslint-disable-next-line
  }, [selectedService]);

  useEffect(() => {
    async function fetchServices() {
      try {
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/service-catalog?cityCode=${cityCode}&tenantId=${tenantID}`;
        console.log('API Request URL:', apiUrl);
        console.log('API Request Parameters:', {
          cityCode,
          tenantId: tenantID,
          REACT_APP_API_URL: process.env.REACT_APP_API_URL
        });

        const res = await fetch(apiUrl);
        console.log('API Response Status:', res.status);
        console.log('API Response Headers:', Object.fromEntries(res.headers.entries()));

        if (!res.ok) {
          const text = await res.text();
          console.error('API Error Response:', {
            status: res.status,
            statusText: res.statusText,
            body: text,
            url: apiUrl,
            headers: Object.fromEntries(res.headers.entries())
          });
          return;
        }

        const data = await res.json();
        console.log('API Response Data:', {
          totalServices: data.length,
          services: data.map(s => ({
            id: s.id,
            name: s.name,
            type: s.type,
            cityCode: s.cityCode,
            tenantId: s.tenantId
          }))
        });
        setServiceCatalog(data);
      } catch (err) {
        console.error('Fetch Error Details:', {
          message: err.message,
          stack: err.stack,
          name: err.name,
          cause: err.cause
        });
      }
    }
    fetchServices();
  }, []);

  // Split main and extra services
  const mainServices = serviceCatalog.filter(s => s.type === 'main');
  const extraServices = serviceCatalog.filter(s => s.type === 'extra');
  
  console.log('Filtered services:', {
    mainServices,
    extraServices,
    totalServices: serviceCatalog.length
  });

  const isPoolCleaning = Array.isArray(formData.mainServiceIds) ? formData.mainServiceIds.includes('POOL_CLEANING') : formData.mainServiceIds === 'POOL_CLEANING';
  const isExteriorCleaning = Array.isArray(formData.mainServiceIds) ? formData.mainServiceIds.includes('EXTERIOR_CLEANING') : formData.mainServiceIds === 'EXTERIOR_CLEANING';

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Step navigation
  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate only customerName, zipcode, and contactInfo
      if (!formData.customerName || !formData.zipcode || !formData.contactInfo) {
        setValidationErrors({
          customerName: !formData.customerName ? 'Name is required' : undefined,
          zipcode: !formData.zipcode ? 'Zipcode is required' : undefined,
          contactInfo: !formData.contactInfo ? 'Contact info is required' : undefined,
        });
        return;
      }
      // Determine if contactInfo is phone or email
      const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.contactInfo);
      const isPhone = /^\+?\d{7,}$/.test(formData.contactInfo.replace(/\D/g, ''));
      let customerPhone = '';
      let customerEmail = '';
      if (isEmail) customerEmail = formData.contactInfo;
      else if (isPhone) customerPhone = formData.contactInfo;
      else {
        setValidationErrors({ contactInfo: 'Please enter a valid phone number or email address.' });
        return;
      }
      // POST to create quotation
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerName: formData.customerName,
            zipcode: formData.zipcode,
            customerPhone,
            customerEmail,
          })
        });
        const data = await response.json();
        if (!response.ok) {
          setError(data.error || 'Failed to create quotation');
          return;
        }
        setQuotationId(data.quotation.id);
        setActiveStep((prev) => prev + 1);
      } catch (err) {
        setError('Failed to create quotation');
      }
    } else if (quotationId) {
      let fieldsToUpdate = {};
      
      switch (activeStep) {
        case 1: // Type of Cleaning
          fieldsToUpdate = {
            mainServiceIds: formData.mainServiceIds
          };
          break;
        case 2: // Property Details
          fieldsToUpdate = {
            propertyType: formData.propertyType,
            bedrooms: formData.bedrooms,
            bathrooms: formData.bathrooms,
            squareFootage: formData.squareFootage
          };
          break;
        case 3: // Combined Frequency & Date
          fieldsToUpdate = {
            frequency: formData.frequency,
            preferredDate: formData.preferredDate,
            preferredTime: formData.preferredTime
          };
          break;
        case 4: // Extras
          fieldsToUpdate = {
            extraServiceIds: formData.extraServiceIds
          };
          break;
        case 5: // Final step: Address, City & Special Instructions
          fieldsToUpdate = {
            address: formData.address,
            city: formData.city,
            specialInstructions: formData.specialInstructions
          };
          break;
      }

      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/quotations/${quotationId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fieldsToUpdate)
        });
        setActiveStep((prev) => prev + 1);
      } catch (err) {
        setError('Failed to update quotation');
      }
    } else {
      setError('Quotation ID missing. Please start from the beginning.');
    }
  };
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Step 1: Card selection
  const handleServiceTypeSelect = (id) => {
    setFormData((prev) => {
      const selected = prev.mainServiceIds.includes(id)
        ? prev.mainServiceIds.filter((v) => v !== id)
        : [...prev.mainServiceIds, id];
      return { ...prev, mainServiceIds: selected };
    });
  };

  // Step 2: Property details change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Step 1: Type of Cleaning
  const renderStep1 = () => {
    console.log('Rendering Step 1 with services:', {
      mainServices,
      selectedServices: formData.mainServiceIds
    });
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Select the type(s) of cleaning you need:</Typography>
        <Grid container spacing={2}>
          {mainServices.map(service => {
            const Icon = iconMap[service.icon] || CleaningServices;
            console.log('Rendering service card:', service);
            return (
              <Grid item xs={12} sm={6} md={3} key={service.id}>
                <Card
                  variant={formData.mainServiceIds.includes(service.id) ? 'outlined' : 'elevation'}
                  sx={{
                    border: formData.mainServiceIds.includes(service.id) ? '2px solid #1976d2' : undefined,
                    minHeight: 160,
                    height: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    cursor: 'pointer',
                    p: 2,
                    overflow: 'hidden',
                  }}
                  onClick={() => handleServiceTypeSelect(service.id)}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        p: 0,
                      }}
                    >
                      <Icon fontSize="large" />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          mt: 1,
                          fontWeight: 600,
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          fontSize: '1.1rem',
                          lineHeight: 1.2,
                          maxWidth: 120,
                        }}
                      >
                        {service.name}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  // Step 2: Property Details
  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Tell us about your property:</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Property Type</InputLabel>
            <Select
              name="propertyType"
              value={formData.propertyType}
              onChange={handleChange}
              required
            >
              {propertyTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Conditionally render these based on propertyType/cleaningType */}
        <Grid item xs={6} sm={3}>
          <TextField
            label="Bedrooms"
            name="bedrooms"
            type="number"
            value={formData.bedrooms}
            onChange={handleChange}
            fullWidth
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Bathrooms"
            name="bathrooms"
            type="number"
            value={formData.bathrooms}
            onChange={handleChange}
            fullWidth
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required>
            <InputLabel>Square Footage</InputLabel>
            <Select
              name="squareFootage"
              value={formData.squareFootage}
              onChange={handleChange}
              label="Square Footage"
            >
              {squareFootageOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Add Pool Size, Yard Size, etc. as needed */}
      </Grid>
    </Box>
  );

  // Step 3: Combined Frequency & Date/Time
  const renderStep3And5 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>How often do you need cleaning and when?</Typography>
      
      {/* Frequency Selection */}
      <Typography variant="subtitle1" sx={{ mb: 2, mt: 3 }}>Frequency:</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {frequencyOptions.map(option => (
          <Grid item xs={6} sm={3} key={option.value}>
            <Button
              variant={formData.frequency === option.value ? 'contained' : 'outlined'}
              color="primary"
              fullWidth
              sx={{ py: 2, fontWeight: 600 }}
              onClick={() => setFormData(prev => ({ ...prev, frequency: option.value }))}
            >
              {option.label}
            </Button>
          </Grid>
        ))}
      </Grid>

      {/* Date & Time Selection */}
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Preferred Date & Time:</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Preferred Date"
            name="preferredDate"
            type="date"
            value={formData.preferredDate}
            onChange={(e) => {
              // Only set the date if it's a valid date string
              const dateStr = e.target.value;
              if (dateStr && !isNaN(new Date(dateStr).getTime())) {
                setFormData(prev => ({ ...prev, preferredDate: dateStr }));
              } else {
                setFormData(prev => ({ ...prev, preferredDate: '' }));
              }
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: new Date().toISOString().split('T')[0] // Set min date to today
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Preferred Time</InputLabel>
            <Select
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              label="Preferred Time"
            >
              <MenuItem value="morning">Morning</MenuItem>
              <MenuItem value="afternoon">Afternoon</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // Step 4: Extras
  const renderStep4 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Would you like to add any extras?</Typography>
      <Grid container spacing={2}>
        {extraServices.map(service => {
          const Icon = iconMap[service.icon] || CleaningServices;
          return (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Card
                variant={formData.extraServiceIds.includes(service.id) ? 'outlined' : 'elevation'}
                sx={{
                  border: formData.extraServiceIds.includes(service.id) ? '2px solid #1976d2' : undefined,
                  minHeight: 160,
                  height: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer',
                  p: 2,
                  overflow: 'hidden',
                }}
                onClick={() => handleExtrasSelect(service.id)}
              >
                <CardActionArea sx={{ height: '100%' }}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      p: 0,
                    }}
                  >
                    <Icon fontSize="large" />
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        fontSize: '1.1rem',
                        lineHeight: 1.2,
                        maxWidth: 140,
                      }}
                    >
                      {service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {service.priceModel === 'per_hour' && `$${service.rate}/hr`}
                      {service.priceModel === 'per_sqft' && `$${service.rate}/sqft`}
                      {service.priceModel === 'per_unit' && `$${service.rate}/unit`}
                      {service.priceModel === 'per_load' && `$${service.rate}/load`}
                      {service.priceModel === 'per_room' && `$${service.rate}/room`}
                      {service.priceModel === 'per_visit' && `$${service.rate}/visit`}
                      {service.priceModel === 'flat' && `$${service.rate} flat`}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // Step 1: Contact Info
  const renderStep6 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Let's get started! Tell us how to contact you:</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Your Name"
            name="customerName"
            value={formData.customerName}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Zipcode"
            name="zipcode"
            value={formData.zipcode}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Phone or Email"
            name="contactInfo"
            value={formData.contactInfo}
            onChange={handleChange}
            fullWidth
            required
            placeholder="Enter your phone number or email address"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const handleEditSection = (stepIndex) => setActiveStep(stepIndex);

  // Step 6: Combined Book & Special Instructions
  const renderStep8 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Where should we send your cleaning team?</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="City"
            name="city"
            value={formData.city}
            onChange={handleChange}
            fullWidth
            required
          />
        </Grid>
      </Grid>

      {/* Special Instructions Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Any special instructions?</Typography>
      <TextField
        label="Special Instructions (allergies, pets, access code, etc.)"
        name="specialInstructions"
        value={formData.specialInstructions}
        onChange={handleChange}
        fullWidth
        multiline
        rows={3}
        placeholder="Optional"
        sx={{ mb: 3 }}
      />

      <EditableInvoiceQuoteCard
        formData={formData}
        getEstimate={getEstimate}
        editable={true}
        onEditSection={handleEditSection}
        serviceCatalog={serviceCatalog}
      />
      {/* Trust & Guarantee Banner */}
      <Box sx={{ my: 3, p: 2, bgcolor: 'success.lighter', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Shield color='success' />
        <Typography fontWeight={600}>100% Satisfaction Guarantee – or your money back!</Typography>
      </Box>
      
      {/* Stripe Elements for Credit Card */}
      <Elements stripe={stripePromise}>
        <StripePaymentForm
          amount={((Number(getEstimate() || 0) * 1.07).toFixed(2))}
          onSuccess={(paymentResult) => createBooking(paymentResult)}
          onError={msg => setError(msg)}
          disabled={booking}
        />
      </Elements>
    </Box>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setValidationErrors({});

    // Get the final estimate
    const estimate = getEstimate();
    if (!estimate) {
      setError('Unable to calculate final price');
      return;
    }

    // Prepare the data to send
    const dataToSend = {
      ...formData,
      ...(isPoolCleaning && {
        bedrooms: undefined,
        bathrooms: undefined,
        squareFootage: undefined
      }),
      ...(user ? {} : { customerName: formData.customerName, customerEmail: formData.customerEmail, customerPhone: formData.customerPhone }),
      price: estimate.total // Include the final price
    };

    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (user) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
      }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quotations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.details) {
          setValidationErrors(
            data.details.reduce((acc, err) => ({
              ...acc,
              [err.param]: err.msg
            }), {})
          );
        } else {
          setError(data.error || 'Failed to request quotation');
        }
        return;
      }

      setSuccess('Quotation request submitted successfully!');
      setFormData({
        mainServiceIds: [],
        extraServiceIds: [],
        propertyType: '',
        bedrooms: 1,
        bathrooms: 1,
        squareFootage: '',
        poolSize: '',
        exteriorFlooringSize: '',
        yardSize: '',
        frequency: '',
        address: '',
        zipcode: '',
        city: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        specialInstructions: '',
        isRecurring: false,
        recurringPattern: null,
        customerName: '',
        contactInfo: '',
      });
      // If guest, show registration prompt
      if (!user) setShowRegister(true);
    } catch (err) {
      setError('An error occurred while submitting the quotation request');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.customerName,
          email: formData.customerEmail,
          password: registerPassword,
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setRegisterError(data.error || 'Registration failed');
        return;
      }
      setRegisterSuccess('Account created! You can now log in to manage your bookings.');
      setShowRegister(false);
    } catch (err) {
      setRegisterError('An error occurred during registration.');
    }
  };

  // Pricing estimation logic
  const getEstimate = () => {
    const { mainServiceIds, extraServiceIds, bedrooms, bathrooms, squareFootage, poolSize, exteriorFlooringSize, yardSize } = formData;
    if (!mainServiceIds || mainServiceIds.length === 0) return null;
    let estimate = 0;
    const parseSqft = (val) => {
      if (!val) return 0;
      if (val.includes('-')) {
        const [min, max] = val.split('-').map(Number);
        return Math.round((min + max) / 2);
      }
      if (val.startsWith('<')) return Number(val.replace('<', ''));
      if (val.endsWith('+')) return Number(val.replace('+', ''));
      return Number(val);
    };

    // Calculate main services
    mainServiceIds.forEach(id => {
      const service = serviceCatalog.find(s => s.id === id);
      if (!service) return;
      switch (service.priceModel) {
        case 'per_hour':
          estimate += service.rate * Math.max(2, (Number(bedrooms) || 1) + (Number(bathrooms) || 1));
          break;
        case 'per_sqft':
          estimate += service.rate * parseSqft(squareFootage);
          break;
        case 'per_visit':
          estimate += service.rate;
          break;
        case 'per_load':
          estimate += service.rate * (formData.loads ? Number(formData.loads) : 1);
          break;
        case 'per_room':
          estimate += service.rate * (Number(bedrooms) || 1);
          break;
        case 'flat':
          estimate += service.rate;
          break;
        default:
          estimate += service.rate;
          break;
      }
    });

    // Calculate extra services
    extraServiceIds.forEach(id => {
      const extra = serviceCatalog.find(s => s.id === id);
      if (!extra) return;
      switch (extra.priceModel) {
        case 'per_hour':
          estimate += extra.rate * 1;
          break;
        case 'per_sqft':
          estimate += extra.rate * parseSqft(squareFootage);
          break;
        case 'per_unit':
          estimate += extra.rate * 1;
          break;
        case 'per_load':
          estimate += extra.rate * (formData.loads ? Number(formData.loads) : 1);
          break;
        case 'per_room':
          estimate += extra.rate * (Number(bedrooms) || 1);
          break;
        case 'flat':
          estimate += extra.rate;
          break;
        default:
          estimate += extra.rate;
          break;
      }
    });
    return estimate > 0 ? estimate.toFixed(2) : null;
  };

  // Helper to determine if square footage should be shown
  const needsSquareFootage = () => {
    const types = Array.isArray(formData.mainServiceIds) ? formData.mainServiceIds : [formData.mainServiceIds];
    return types.some(type => [
      'GENERAL_CLEANING',
      'DEEP_CLEANING',
      'MOVE_IN_OUT',
      'POST_CONSTRUCTION',
      'WINDOW_CLEANING',
    ].includes(type));
  };

  // Helper to determine if bedrooms/bathrooms should be shown
  const needsBedroomsBathrooms = () => {
    const types = Array.isArray(formData.mainServiceIds) ? formData.mainServiceIds : [formData.mainServiceIds];
    return types.some(type => [
      'GENERAL_CLEANING',
      'DEEP_CLEANING',
      'MOVE_IN_OUT',
      'POST_CONSTRUCTION'
    ].includes(type));
  };

  // Add new function to create quotation
  const createQuotation = async () => {
    try {
      // Get the current estimate
      const estimate = getEstimate();
      if (!estimate) {
        setError('Unable to calculate price');
        return null;
      }

      const dataToSend = {
        ...formData,
        ...(isPoolCleaning && {
          bedrooms: undefined,
          bathrooms: undefined,
          squareFootage: undefined
        }),
        ...(user ? {} : { customerName: formData.customerName, customerEmail: formData.customerEmail, customerPhone: formData.customerPhone }),
        price: estimate.total
      };

      const headers = {
        'Content-Type': 'application/json',
      };
      if (user) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quotations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create quotation');
      }

      return data.quotation;
    } catch (err) {
      setError('Failed to create quotation');
      return null;
    }
  };

  // Create booking after successful payment
  const createBooking = async (paymentResult = null) => {
    try {
      setBooking(true);
      
      // Convert time to 24-hour format
      const convertTo24Hour = (time12h) => {
        if (time12h === 'morning') return '09:00';
        if (time12h === 'afternoon') return '14:00';
        return time12h;
      };
      
      // Prepare booking data
      const bookingData = {
        customerName: formData.customerName,
        contactInfo: formData.contactInfo,
        address: formData.address,
        city: formData.city,
        zipcode: formData.zipcode,
        mainServiceIds: formData.mainServiceIds,
        extraServiceIds: formData.extraServiceIds,
        propertyType: formData.propertyType,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        squareFootage: formData.squareFootage,
        frequency: formData.frequency,
        preferredDate: formData.preferredDate,
        preferredTime: convertTo24Hour(formData.preferredTime),
        specialInstructions: formData.specialInstructions,
        totalAmount: ((Number(getEstimate() || 0) * 1.07).toFixed(2)),
        paymentStatus: paymentResult ? 'PAID' : 'PENDING',
        paymentMethod: 'CREDIT_CARD',
        ...(paymentResult && { paymentIntentId: paymentResult.paymentIntentId })
      };

      // Create booking via API
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/create-voucher-booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create booking');
      }

      const bookingResponse = await response.json();
      
      // Navigate to BookingSuccess page with booking data
      navigate('/booking-success', {
        state: {
          booking: bookingResponse.booking,
          paymentStatus: paymentResult ? 'paid' : 'pending',
          totalAmount: bookingData.totalAmount
        }
      });

    } catch (error) {
      console.error('Booking creation error:', error);
      setError(error.message || 'Failed to create booking');
      setBooking(false);
    }
  };

  const handleExtrasSelect = (id) => {
    setFormData(prev => {
      const selected = prev.extraServiceIds.includes(id)
        ? prev.extraServiceIds.filter(e => e !== id)
        : [...prev.extraServiceIds, id];
      return { ...prev, extraServiceIds: selected };
    });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, overflowX: 'hidden' }}>
        {/* Page Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Request a Detailed Quote
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Get a personalized cleaning quote in just a few steps
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} lg={9}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
              {/* Compact Progress Indicator */}
              <Box sx={{ mb: 4, overflowX: 'auto' }}>
                {isMobile ? (
                  // Mobile: Show current step info instead of full stepper
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Step {activeStep + 1} of {steps.length}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {steps[activeStep]}
                    </Typography>
                    <Box sx={{ 
                      width: '100%', 
                      height: 4, 
                      bgcolor: 'grey.300', 
                      borderRadius: 2, 
                      mt: 2,
                      overflow: 'hidden'
                    }}>
                      <Box sx={{ 
                        width: `${((activeStep + 1) / steps.length) * 100}%`, 
                        height: '100%', 
                        bgcolor: 'primary.main',
                        borderRadius: 2,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                  </Box>
                ) : (
                  // Desktop: Simplified stepper with shorter labels
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel
                    sx={{ 
                      '& .MuiStepLabel-root': {
                        '& .MuiStepLabel-label': {
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          maxWidth: '60px',
                          textAlign: 'center',
                          lineHeight: 1.1,
                          wordBreak: 'break-word'
                        }
                      }
                    }}
                  >
                    {['Contact', 'Type', 'Property', 'Freq & Date', 'Extras', 'Book'].map((label, index) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                )}
              </Box>

              {/* Step Content */}
              {activeStep === 0 && renderStep6()}
              {activeStep === 1 && renderStep1()}
              {activeStep === 2 && renderStep2()}
              {activeStep === 3 && renderStep3And5()}
              {activeStep === 4 && renderStep4()}
              {activeStep === 5 && renderStep8()}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  color="primary"
                  size="large"
                >
                  Back
                </Button>
                {/* Only show Next button if not on last step */}
                {activeStep < steps.length - 1 && (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ minWidth: 120 }}
                    disabled={
                      (activeStep === 0 && (
                        !formData.customerName ||
                        !formData.zipcode ||
                        !formData.contactInfo ||
                        validationErrors.customerName ||
                        validationErrors.zipcode ||
                        validationErrors.contactInfo
                      )) ||
                      (activeStep === 1 && formData.mainServiceIds.length === 0) ||
                      (activeStep === 2 && !formData.propertyType) ||
                      (activeStep === 3 && (!formData.frequency || !formData.preferredDate || !formData.preferredTime))
                    }
                  >
                    Next
                  </Button>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Post-booking registration for guests */}
              {showRegister && !user && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Create an account to manage your bookings and get updates
                  </Typography>
                  <form onSubmit={handleRegister}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Customer Name"
                          value={formData.customerName}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Customer Email"
                          value={formData.customerEmail}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          label="Create Password"
                          type={showPassword ? 'text' : 'password'}
                          value={registerPassword}
                          onChange={e => setRegisterPassword(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      {registerError && (
                        <Grid item xs={12}>
                          <Alert severity="error">
                            {registerError}
                            {registerError === 'Email already registered' && (
                              <>
                                {' '}
                                <Button
                                  color="primary"
                                  onClick={() => {
                                    setShowRegister(false);
                                    navigate('/login');
                                  }}
                                  sx={{ textTransform: 'none', ml: 1 }}
                                >
                                  Log in
                                </Button>
                              </>
                            )}
                          </Alert>
                        </Grid>
                      )}
                      {registerSuccess && (
                        <Grid item xs={12}>
                          <Alert severity="success">{registerSuccess}</Alert>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Button type="submit" variant="contained" color="primary" fullWidth>
                          Create Account
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              )}
            </Paper>
          </Grid>
          
              <Grid item xs={12} md={5}>
            {activeStep < steps.length - 1 && (
              <Box
                sx={{
                  maxWidth: 360,
                  width: '100%',
                  mx: 'auto',
                  px: { xs: 1, sm: 2 },
                  position: { md: 'sticky' },
                  top: { md: 32 },
                }}
              >
                <QuoteSummaryCard 
                    formData={formData} 
                    getEstimate={getEstimate} 
                    serviceCatalog={serviceCatalog} 
                  />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

    </Box>
  );
};

export default QuotationRequest; 